import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ScanBarcode,
  Info,
  CheckCircle2,
  AlertCircle,
  X,
  Wand2,
  Zap,
  ListFilter,
  PlusCircle,
  TrendingDown,
} from "lucide-react";
import ReactSelect from "react-select";
import { toast } from "sonner";
import PropTypes from "prop-types";

/**
 * Universal Serial Numbers Modal
 * Used across the entire application for:
 * - GRN (Goods Received Notes: new & edit)
 * - Opening Stocks
 * - Stock Adjustments & Product Stock Adjustments (Additions & Removals)
 * - Product Creation Opening Stocks
 */
const AddSerialsModal = ({
  isOpen,
  onClose,
  onSave,
  initialSerials = [],
  quantity = 0,
  productName = "Product",
  excludeSerials = [],
  availableSerials = [], // Existing serials in stock for removals
  adjustmentType, // "addition" | "removal" (optional)
  onAdjustmentTypeChange, // Callback if adjustment type can be switched in modal
  allowQuantityEdit = false,
  onQuantityChange,
}) => {
  const [currentQty, setCurrentQty] = useState(
    Math.max(0, parseInt(quantity) || 0)
  );
  const [activeType, setActiveType] = useState(adjustmentType || "addition");

  const targetQty = currentQty;
  const isUnlimited = targetQty >= 999999 || targetQty === 0;

  const [serialsText, setSerialsText] = useState("");
  const [serialsList, setSerialsList] = useState([]);
  const [error, setError] = useState(null);

  // Configuration for sequence generator:
  // start is preserved as a string (e.g. "1" or "0001") to retain leading zeros
  const [serialGenConfig, setSerialGenConfig] = useState({
    prefix: "SN",
    start: "1",
    count: !isUnlimited && targetQty > 0 ? targetQty : 1,
  });

  const isSelectionMode =
    activeType === "removal" ||
    (Boolean(availableSerials && availableSerials.length > 0) &&
      activeType !== "addition");

  // Normalize initial serials into array of strings
  const normalizeSerials = (raw) => {
    if (!raw) return [];
    if (typeof raw === "string") {
      return raw
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (Array.isArray(raw)) {
      return raw
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return item.serial_number || item.serial || "";
          }
          return String(item || "").trim();
        })
        .filter(Boolean);
    }
    return [];
  };

  useEffect(() => {
    if (isOpen) {
      const parsedQty = Math.max(0, parseInt(quantity) || 0);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentQty(parsedQty);
      if (adjustmentType) {
        setActiveType(adjustmentType);
      }

      const normalized = normalizeSerials(initialSerials);
      setSerialsList(normalized);
      setSerialsText(normalized.join("\n"));
      setError(null);

      const unlimited = parsedQty >= 999999 || parsedQty === 0;
      const remainingNeeded =
        !unlimited && parsedQty > 0
          ? Math.max(0, parsedQty - normalized.length)
          : 1;

      setSerialGenConfig((prev) => ({
        ...prev,
        count: remainingNeeded > 0 ? remainingNeeded : parsedQty || 1,
      }));
    }
  }, [isOpen, initialSerials, quantity, adjustmentType]);

  const handleTypeSwitch = (newType) => {
    setActiveType(newType);
    if (onAdjustmentTypeChange) {
      onAdjustmentTypeChange(newType);
    }
    // Clear current serials when switching type
    setSerialsList([]);
    setSerialsText("");
    setError(null);
  };

  // Sequence generator with exact prefix and starting number formatting
  const generateSerials = () => {
    const qtyToGen = parseInt(serialGenConfig.count);
    if (isNaN(qtyToGen) || qtyToGen <= 0) {
      toast.error("Please enter a valid count to generate.");
      return;
    }

    if (!isUnlimited && targetQty > 0) {
      const remainingNeeded = targetQty - serialsList.length;
      if (remainingNeeded <= 0) {
        toast.error(
          `Target quantity of ${targetQty} serials has already been reached.`
        );
        return;
      }
      if (qtyToGen > remainingNeeded) {
        toast.error(
          `Cannot generate ${qtyToGen} serials. Only ${remainingNeeded} more needed for mentioned quantity (${targetQty}).`
        );
        return;
      }
    }

    // Preserve string formatting: "1" has length 1 -> SN1; "0001" has length 4 -> SN0001
    const startStr =
      serialGenConfig.start !== "" && serialGenConfig.start !== undefined
        ? String(serialGenConfig.start)
        : "1";
    const padLength = startStr.length;
    const startNum = parseInt(startStr, 10) || 1;
    const prefix = serialGenConfig.prefix || "";

    const generated = [...serialsList];
    let skippedCount = 0;
    let i = 0;
    let currentNum = startNum;

    while (i < qtyToGen) {
      if (!isUnlimited && targetQty > 0 && generated.length >= targetQty) {
        break;
      }

      const numStr = String(currentNum).padStart(padLength, "0");
      const serial = `${prefix}${numStr}`;

      if (generated.includes(serial) || excludeSerials.includes(serial)) {
        skippedCount++;
      } else {
        generated.push(serial);
        i++;
      }
      currentNum++;

      // Safety break to prevent infinite loop
      if (currentNum > startNum + qtyToGen + 1000 + excludeSerials.length) {
        break;
      }
    }

    const newText = generated.join("\n");
    setSerialsText(newText);
    setSerialsList(generated);
    setError(null);

    const remainingAfter =
      !isUnlimited && targetQty > 0
        ? Math.max(0, targetQty - generated.length)
        : 1;

    const nextStartStr = String(currentNum).padStart(padLength, "0");
    setSerialGenConfig((prev) => ({
      ...prev,
      start: nextStartStr,
      count: remainingAfter > 0 ? remainingAfter : 1,
    }));

    if (skippedCount > 0) {
      toast.success(
        `Generated ${i} serial numbers. Skipped ${skippedCount} existing/reserved serials.`
      );
    } else {
      toast.success(`Generated ${i} serial numbers`);
    }
  };

  const clearSerials = () => {
    setSerialsText("");
    setSerialsList([]);
    setError(null);
    setSerialGenConfig((prev) => ({
      ...prev,
      count: !isUnlimited && targetQty > 0 ? targetQty : 1,
    }));
    toast.info("All serial numbers cleared");
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setSerialsText(text);

    // Parse serials (one per line or comma separated)
    const list = text
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    setSerialsList(list);

    // Check for duplicates
    const hasInternalDuplicates = new Set(list).size !== list.length;
    const duplicatesInExcluded = list.filter((s) => excludeSerials.includes(s));

    if (hasInternalDuplicates) {
      setError("Duplicate serial numbers detected in your entry.");
    } else if (duplicatesInExcluded.length > 0) {
      setError(
        `Some serial numbers are already in use or reserved: ${duplicatesInExcluded
          .slice(0, 3)
          .join(", ")}${duplicatesInExcluded.length > 3 ? "..." : ""}`
      );
    } else if (!isUnlimited && targetQty > 0 && list.length > targetQty) {
      setError(
        `Too many serials entered (${list.length} / ${targetQty}). Only ${targetQty} serials can be entered for the mentioned quantity.`
      );
    } else {
      setError(null);
    }
  };

  // Quantity editing (when allowed)
  const handleQuantityEdit = (newVal) => {
    const parsed = Math.max(1, parseInt(newVal) || 1);
    setCurrentQty(parsed);
    if (onQuantityChange) {
      onQuantityChange(parsed, activeType);
    }
    // Re-validate against new quantity
    if (serialsList.length > parsed) {
      setError(
        `Too many serials entered (${serialsList.length} / ${parsed}). Only ${parsed} serials allowed.`
      );
    } else {
      setError(null);
    }
  };

  const handleSave = () => {
    const uniqueSerials = new Set(serialsList);
    if (uniqueSerials.size !== serialsList.length) {
      toast.error("Duplicate serial numbers detected in your entry.");
      return;
    }

    const duplicatesInExcluded = serialsList.filter((s) =>
      excludeSerials.includes(s)
    );
    if (duplicatesInExcluded.length > 0) {
      toast.error(
        `Some serial numbers are already in use: ${duplicatesInExcluded[0]}`
      );
      return;
    }

    if (!isUnlimited && targetQty > 0) {
      if (serialsList.length < targetQty) {
        toast.error(
          `Please provide ${targetQty} serial numbers (${serialsList.length} entered, ${targetQty - serialsList.length
          } remaining).`
        );
        return;
      }
      if (serialsList.length > targetQty) {
        toast.error(
          `Cannot save: ${serialsList.length} serials entered, but mentioned quantity is ${targetQty}.`
        );
        return;
      }
    }

    onSave(serialsList, currentQty, activeType);
    onClose();
  };

  // Pre-calculate last available serial for removal hints
  const lastAvailableSerial = useMemo(() => {
    if (!availableSerials || availableSerials.length === 0) return null;
    const sorted = [...availableSerials].sort((a, b) => {
      const sa = typeof a === "object" ? a.serial_number : a;
      const sb = typeof b === "object" ? b.serial_number : b;
      return String(sa).localeCompare(String(sb), undefined, { numeric: true });
    });
    const last = sorted[sorted.length - 1];
    return typeof last === "object" ? last.serial_number : last;
  }, [availableSerials]);

  const isMatched =
    !isUnlimited && targetQty > 0 && serialsList.length === targetQty;
  const isOver =
    !isUnlimited && targetQty > 0 && serialsList.length > targetQty;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-6 bg-primary text-primary-foreground">
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="h-5 w-5" />
            {isSelectionMode ? "Select Serial Numbers" : "Add Serial Numbers"}
          </DialogTitle>
          <DialogDescription className="text-primary-foreground/80 text-xs">
            {isSelectionMode
              ? "Select existing serial numbers from stock for removal."
              : "Manage, auto-generate, or scan unique serial numbers for this product."}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {/* Optional Adjustment Type Selector */}
          {Boolean(onAdjustmentTypeChange) && (
            <div className="space-y-2 pb-3 border-b">
              <Label className="text-xs font-bold text-muted-foreground uppercase">
                Adjustment Type
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={activeType === "addition" ? "default" : "outline"}
                  className={`h-14 flex flex-col items-center justify-center gap-1 ${activeType === "addition"
                      ? "border-primary bg-primary text-primary-foreground font-bold"
                      : "hover:bg-muted"
                    }`}
                  onClick={() => handleTypeSwitch("addition")}
                >
                  <PlusCircle className="h-5 w-5" />
                  <span className="text-xs">Add Stock</span>
                </Button>
                <Button
                  type="button"
                  variant={activeType === "removal" ? "destructive" : "outline"}
                  className={`h-14 flex flex-col items-center justify-center gap-1 ${activeType === "removal"
                      ? "border-destructive bg-destructive text-destructive-foreground font-bold"
                      : "hover:bg-muted"
                    }`}
                  onClick={() => handleTypeSwitch("removal")}
                >
                  <TrendingDown className="h-5 w-5" />
                  <span className="text-xs">Remove Stock</span>
                </Button>
              </div>
            </div>
          )}

          {/* Header Card: Product Info & Mentioned Quantity */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
            <div>
              <p
                className="text-sm font-semibold truncate max-w-[200px]"
                title={productName}
              >
                {productName}
              </p>
              {!isUnlimited && targetQty > 0 && !allowQuantityEdit && (
                <p className="text-xs text-muted-foreground">
                  Required: {targetQty} unit{targetQty !== 1 ? "s" : ""}
                </p>
              )}
            </div>

            {allowQuantityEdit ? (
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <Label className="text-[10px] text-muted-foreground block">
                    Mentioned Qty
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    value={targetQty}
                    onChange={(e) => handleQuantityEdit(e.target.value)}
                    className="w-20 h-7 text-xs font-bold text-right"
                  />
                </div>
                <span className="text-xs font-medium text-muted-foreground pt-3">
                  Units
                </span>
              </div>
            ) : (
              <Badge
                variant={
                  isMatched ? "default" : isOver ? "destructive" : "secondary"
                }
                className={`text-xs px-2 py-0.5 font-bold ${isMatched
                    ? "bg-green-600 hover:bg-green-600 text-white"
                    : isOver
                      ? "bg-destructive text-destructive-foreground"
                      : ""
                  }`}
              >
                {serialsList.length} / {targetQty > 0 ? targetQty : "∞"}
              </Badge>
            )}
          </div>

          {/* Mode 1: Select Existing Serials (e.g. Stock Adjustment Removal) */}
          {isSelectionMode ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold flex items-center gap-1.5">
                  <ListFilter className="h-3.5 w-3.5 text-primary" />
                  Available Serials in Stock
                </Label>
                {serialsList.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSerials}
                    className="h-6 px-2 text-[10px] text-destructive hover:bg-destructive/10"
                  >
                    Clear Selection
                  </Button>
                )}
              </div>

              <ReactSelect
                isMulti
                options={availableSerials.map((s) => ({
                  value: typeof s === "object" ? s.serial_number : s,
                  label: typeof s === "object" ? s.serial_number : s,
                  status: s.status_info?.label,
                  variant: s.status_info?.variant,
                }))}
                formatOptionLabel={(option) => (
                  <div className="flex justify-between items-center w-full min-w-[200px]">
                    <span className="font-mono text-sm">{option.label}</span>
                    {option.status && (
                      <Badge
                        variant={option.variant || "secondary"}
                        className="text-[10px] h-4"
                      >
                        {option.status}
                      </Badge>
                    )}
                  </div>
                )}
                value={serialsList.map((sn) => ({ value: sn, label: sn }))}
                onChange={(selectedOptions) => {
                  const vals = (selectedOptions || []).map((o) => o.value);
                  if (!isUnlimited && targetQty > 0 && vals.length > targetQty) {
                    toast.error(
                      `Cannot select more than ${targetQty} serials for mentioned quantity.`
                    );
                    return;
                  }
                  setSerialsList(vals);
                  setSerialsText(vals.join("\n"));
                  setError(null);
                }}
                placeholder="Search and select serial numbers..."
                className="text-xs"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                <span>
                  {availableSerials.length} serial
                  {availableSerials.length !== 1 ? "s" : ""} available in stock.
                </span>
                {lastAvailableSerial && (
                  <span className="font-semibold text-primary">
                    Last: {lastAvailableSerial}
                  </span>
                )}
              </div>
            </div>
          ) : (
            /* Mode 2: Add / Auto-Generate / Manual Enter Serials */
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="serials-input">Serial Numbers</Label>
                <div className="flex items-center gap-2">
                  {serialsList.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearSerials}
                      className="h-7 px-2 text-[10px] text-destructive hover:text-destructive hover:bg-destructive/10 gap-1 font-bold"
                    >
                      <X className="h-3 w-3" />
                      Clear All
                    </Button>
                  )}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[10px] text-primary hover:text-primary/80 hover:bg-primary/10 gap-1 font-bold"
                      >
                        <Wand2 className="h-3 w-3" />
                        Auto-Generate
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-68 p-4" align="end">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="h-4 w-4 text-amber-500" />
                          <span className="text-xs font-bold">
                            Sequence Generator
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[10px]">Prefix</Label>
                            <Input
                              className="h-8 text-xs font-mono"
                              placeholder="e.g. SN"
                              value={serialGenConfig.prefix}
                              onChange={(e) =>
                                setSerialGenConfig({
                                  ...serialGenConfig,
                                  prefix: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Start No.</Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="1 or 0001"
                              className="h-8 text-xs font-mono"
                              value={serialGenConfig.start}
                              onChange={(e) => {
                                const digitsOnly = e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                                setSerialGenConfig({
                                  ...serialGenConfig,
                                  start: digitsOnly,
                                });
                              }}
                            />
                          </div>
                          <div className="space-y-1 col-span-2">
                            <div className="flex justify-between items-center">
                              <Label className="text-[10px]">
                                Count to Generate
                              </Label>
                              {!isUnlimited && targetQty > 0 && (
                                <span className="text-[10px] text-muted-foreground font-medium">
                                  Needed:{" "}
                                  {Math.max(
                                    0,
                                    targetQty - serialsList.length
                                  )}
                                </span>
                              )}
                            </div>
                            <Input
                              type="number"
                              className="h-8 text-xs"
                              value={serialGenConfig.count}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                setSerialGenConfig({
                                  ...serialGenConfig,
                                  count: isNaN(val) ? "" : val,
                                });
                              }}
                              min={1}
                              max={
                                !isUnlimited && targetQty > 0
                                  ? Math.max(1, targetQty - serialsList.length)
                                  : undefined
                              }
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          className="w-full text-xs font-semibold"
                          disabled={
                            !isUnlimited &&
                            targetQty > 0 &&
                            serialsList.length >= targetQty
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            generateSerials();
                          }}
                        >
                          {!isUnlimited &&
                            targetQty > 0 &&
                            serialsList.length >= targetQty
                            ? "Target Reached"
                            : "Generate Sequence"}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <Textarea
                id="serials-input"
                placeholder="Scan or type serial numbers here (one per line or comma-separated)..."
                className={`min-h-[140px] font-mono text-xs whitespace-pre ${error ? "border-destructive focus-visible:ring-destructive" : ""
                  }`}
                value={serialsText}
                onChange={handleTextChange}
              />
            </div>
          )}

          {/* Validation & Feedback Status */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-xs font-medium bg-destructive/10 p-2.5 rounded-md border border-destructive/20">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!error &&
            !isUnlimited &&
            targetQty > 0 &&
            serialsList.length < targetQty && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-medium bg-amber-50 dark:bg-amber-900/10 p-2.5 rounded-md border border-amber-200/50">
                <Info className="h-4 w-4 shrink-0" />
                <span>
                  {serialsList.length} of {targetQty} serials entered (
                  {targetQty - serialsList.length} more needed).
                </span>
              </div>
            )}

          {((isUnlimited && serialsList.length > 0) || isMatched) &&
            !error && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-xs font-medium bg-green-50 dark:bg-green-900/10 p-2.5 rounded-md border border-green-200/50">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>
                  All {targetQty > 0 ? targetQty : ""} serial numbers entered
                  correctly.
                </span>
              </div>
            )}
        </div>

        <DialogFooter className="p-6 pt-0 gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !!error ||
              (!isUnlimited &&
                targetQty > 0 &&
                serialsList.length !== targetQty)
            }
          >
            Save Serials
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AddSerialsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialSerials: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  quantity: PropTypes.number,
  productName: PropTypes.string,
  excludeSerials: PropTypes.array,
  availableSerials: PropTypes.array,
  adjustmentType: PropTypes.oneOf(["addition", "removal"]),
  onAdjustmentTypeChange: PropTypes.func,
  allowQuantityEdit: PropTypes.bool,
  onQuantityChange: PropTypes.func,
};

export default AddSerialsModal;
